import { useEffect, useMemo, useState } from "react";
import { Sparkles } from "lucide-react";
import api from "@/lib/api";

function ClientTile({ client }) {
  const initials = client.name
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");

  // Prefer an uploaded logo, then Clearbit if a domain is set, else monogram
  const src = client.logo_url || (client.domain ? `https://logo.clearbit.com/${client.domain}?size=200` : null);

  return (
    <div
      className="group relative shrink-0 w-[220px] md:w-[240px] h-[120px] rounded-2xl border border-border/70 bg-card flex items-center justify-center px-6 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(15,23,42,0.35)] hover:border-primary/50 hover:z-20"
      data-testid={`client-tile-${initials}`}
    >
      {src ? (
        <img
          src={src}
          alt={client.name}
          loading="lazy"
          className="max-h-20 md:max-h-24 max-w-[80%] object-contain transition-transform duration-300 ease-out group-hover:scale-[1.75]"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className="flex items-center gap-3 transition-transform duration-300 ease-out group-hover:scale-[1.35]">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary font-heading font-black text-lg flex items-center justify-center shrink-0">
            {initials}
          </div>
          <div className="font-heading font-semibold text-sm leading-tight text-foreground/80 line-clamp-2">
            {client.name}
          </div>
        </div>
      )}

      {/* Client name tag revealed on hover */}
      <div
        className="pointer-events-none absolute left-1/2 -bottom-3 -translate-x-1/2 translate-y-1 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 ease-out"
        aria-hidden="true"
      >
        <div className="px-3 py-1.5 rounded-full bg-foreground text-background text-[11px] font-mono uppercase tracking-[0.18em] whitespace-nowrap shadow-lg">
          {client.name}
        </div>
      </div>
    </div>
  );
}

function Row({ list, reverse = false }) {
  // Duplicate list once so the CSS marquee wraps seamlessly.
  const loop = useMemo(() => [...list, ...list], [list]);
  return (
    <div className="marquee-pausable relative overflow-x-hidden overflow-y-visible py-6">
      <div
        className="flex gap-4 md:gap-5 w-max animate-marquee"
        style={{
          animationDuration: "90s",
          ...(reverse ? { animationDirection: "reverse" } : {}),
        }}
      >
        {loop.map((c, i) => <ClientTile key={`${c.name}-${i}`} client={c} />)}
      </div>
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  if (clients.length === 0) return null;

  const half = Math.ceil(clients.length / 2);
  const rowA = clients.slice(0, half);
  const rowB = clients.slice(half);

  return (
    <section className="relative py-12 md:py-16" data-testid="clients-section">
      <div className="max-w-7xl mx-auto px-6 mb-10 md:mb-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-mono uppercase tracking-[0.28em] text-primary">Trusted since 2012</span>
            </div>
            <h2 className="mt-4 font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              The brands who put their name behind ours.
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-xl">
              From FMCG giants and international news channels to universities, hospitals and government agencies — 500+ organisations across four continents.
            </p>
          </div>
          <div className="flex items-baseline gap-3 shrink-0">
            <div
              className="font-heading font-black italic text-transparent leading-none"
              style={{
                WebkitTextStroke: "1.5px hsl(var(--foreground) / 0.35)",
                fontSize: "clamp(4.5rem, 10vw, 8rem)",
              }}
            >
              500+
            </div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground pb-2">
              Clients<br/>Delivered
            </div>
          </div>
        </div>
      </div>

      {/* Marquee constrained inside the same boxed layout so it fades at the container edges, not viewport edges */}
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-32 z-10"
               style={{ background: "linear-gradient(90deg, hsl(var(--background)) 0%, transparent 100%)" }} />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-32 z-10"
               style={{ background: "linear-gradient(270deg, hsl(var(--background)) 0%, transparent 100%)" }} />
          <div className="space-y-5">
            <Row list={rowA} />
            <Row list={rowB} reverse />
          </div>
        </div>
      </div>
    </section>
  );
}
